import React, { createContext, useContext, useReducer, ReactNode, useEffect } from 'react';
import { saveUserBio, markOnboardingCompleted } from '@orbit/shared/auth/user';
import { UserBio } from '@orbit/shared/types';
import { FirebaseApp } from 'firebase/app';
import { BusinessIdea } from '../components/onboarding/onboarding.types';

// Onboarding step types
export type OnboardingStep =
  | 'initial-selection'
  | 'user-business-idea'
  | 'user-bio-form'
  | 'processing'
  | 'error'
  | 'business-ideas-selection'
  | 'business-idea-review'
  | 'setup';

// Step name constants for type-safe navigation
export const ONBOARDING_STEPS = {
  INITIAL_SELECTION: 'initial-selection' as const,
  USER_BUSINESS_IDEA: 'user-business-idea' as const,
  USER_BIO_FORM: 'user-bio-form' as const,
  PROCESSING: 'processing' as const,
  ERROR: 'error' as const,
  BUSINESS_IDEAS_SELECTION: 'business-ideas-selection' as const,
  BUSINESS_IDEA_REVIEW: 'business-idea-review' as const,
  SETUP: 'setup' as const,
} as const;

// User response types
export interface OnboardingData {
  initialChoice?: 'existing-idea' | 'need-ideas';
  businessIdea?: Partial<BusinessIdea>;
  userBio?: {
    // Will be defined when we build the UserBioForm
    [key: string]: any;
  };
  selectedBusinessIdea?: {
    id: string;
    name: string;
    description: string;
    metadata?: any;
  };
  processingError?: {
    message: string;
    code?: string;
  };
  // Cache generated business ideas to avoid regeneration
  generatedBusinessIdeas?: BusinessIdea[];
  // Track what type of processing is happening
  processingType?:
    | 'enhancing-business-idea'
    | 'generating-business-ideas'
    | 'selected-from-generated';
}

// Context state
interface OnboardingState {
  currentStep: OnboardingStep;
  data: OnboardingData;
  isLoading: boolean;
  error: string | null;
}

// Action types
type OnboardingAction =
  | { type: 'SET_STEP'; payload: OnboardingStep }
  | { type: 'SET_DATA'; payload: Partial<OnboardingData> }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'RESET' };

// Initial state
const initialState: OnboardingState = {
  currentStep: 'initial-selection',
  data: {},
  isLoading: false,
  error: null,
};

// Reducer
function onboardingReducer(state: OnboardingState, action: OnboardingAction): OnboardingState {
  switch (action.type) {
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };
    case 'SET_DATA':
      return { ...state, data: { ...state.data, ...action.payload } };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// Context
interface OnboardingContextType {
  state: OnboardingState;
  setStep: (step: OnboardingStep) => void;
  setData: (data: Partial<OnboardingData>) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  saveUserBioToFirestore: (userId: string, rawUserBio: any, app: FirebaseApp) => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

// Provider component
interface OnboardingProviderProps {
  children: ReactNode;
}

const LOCAL_STORAGE_KEY = 'onboardingState';

export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({ children }) => {
  // Load initial state from localStorage if available
  const getInitialState = (): OnboardingState => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(LOCAL_STORAGE_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    }
    return initialState;
  };

  const [state, dispatch] = useReducer(onboardingReducer, undefined, getInitialState);

  // Persist state to localStorage on change
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
    }
  }, [state]);

  const setStep = (step: OnboardingStep) => {
    dispatch({ type: 'SET_STEP', payload: step });
  };

  const setData = (data: Partial<OnboardingData>) => {
    dispatch({ type: 'SET_DATA', payload: data });
  };

  const setLoading = (loading: boolean) => {
    dispatch({ type: 'SET_LOADING', payload: loading });
  };

  const setError = (error: string | null) => {
    dispatch({ type: 'SET_ERROR', payload: error });
  };

  const reset = () => {
    dispatch({ type: 'RESET' });
  };

  const goToNextStep = () => {
    // Simple linear step mapping - no branching logic
    const nextStepMap: Record<OnboardingStep, OnboardingStep> = {
      [ONBOARDING_STEPS.INITIAL_SELECTION]: ONBOARDING_STEPS.USER_BIO_FORM, // Default - will be overridden by step-specific logic
      [ONBOARDING_STEPS.USER_BUSINESS_IDEA]: ONBOARDING_STEPS.BUSINESS_IDEA_REVIEW,
      [ONBOARDING_STEPS.BUSINESS_IDEA_REVIEW]: ONBOARDING_STEPS.USER_BIO_FORM,
      [ONBOARDING_STEPS.USER_BIO_FORM]: ONBOARDING_STEPS.PROCESSING,
      [ONBOARDING_STEPS.PROCESSING]: ONBOARDING_STEPS.BUSINESS_IDEAS_SELECTION,
      [ONBOARDING_STEPS.BUSINESS_IDEAS_SELECTION]: ONBOARDING_STEPS.SETUP,
      [ONBOARDING_STEPS.SETUP]: ONBOARDING_STEPS.SETUP, // End state
      [ONBOARDING_STEPS.ERROR]: ONBOARDING_STEPS.INITIAL_SELECTION, // Reset on error
    };

    const nextStep = nextStepMap[state.currentStep];
    if (nextStep) {
      // Set processing type when going from user-bio-form to processing
      if (
        state.currentStep === ONBOARDING_STEPS.USER_BIO_FORM &&
        nextStep === ONBOARDING_STEPS.PROCESSING
      ) {
        setData({ processingType: 'generating-business-ideas' });
      }
      setStep(nextStep);
    }
  };

  const goToPreviousStep = () => {
    // Explicit step mapping - each step knows exactly where it goes back to
    const previousStepMap: Record<OnboardingStep, OnboardingStep> = {
      [ONBOARDING_STEPS.INITIAL_SELECTION]: ONBOARDING_STEPS.INITIAL_SELECTION, // Can't go back from start
      [ONBOARDING_STEPS.USER_BUSINESS_IDEA]: ONBOARDING_STEPS.INITIAL_SELECTION,
      [ONBOARDING_STEPS.BUSINESS_IDEA_REVIEW]: ONBOARDING_STEPS.BUSINESS_IDEAS_SELECTION,
      [ONBOARDING_STEPS.USER_BIO_FORM]: ONBOARDING_STEPS.INITIAL_SELECTION,
      [ONBOARDING_STEPS.PROCESSING]: ONBOARDING_STEPS.USER_BIO_FORM,
      [ONBOARDING_STEPS.BUSINESS_IDEAS_SELECTION]: ONBOARDING_STEPS.USER_BIO_FORM,
      [ONBOARDING_STEPS.SETUP]: ONBOARDING_STEPS.BUSINESS_IDEAS_SELECTION,
      [ONBOARDING_STEPS.ERROR]: ONBOARDING_STEPS.INITIAL_SELECTION, // Reset on error
    };

    const previousStep = previousStepMap[state.currentStep];
    if (previousStep) {
      setStep(previousStep);

      // Clear initialChoice when going back to initial-selection to prevent stale state
      if (previousStep === ONBOARDING_STEPS.INITIAL_SELECTION) {
        setData({ initialChoice: undefined });
      }
    }
  };

  const saveUserBioToFirestore = async (
    userId: string,
    rawUserBio: any,
    app: FirebaseApp,
  ): Promise<void> => {
    try {
      // Save to Firestore
      await saveUserBio(userId, rawUserBio, app);

      // Mark onboarding as complete
      await markOnboardingCompleted(userId, app);
    } catch (error) {
      console.error('Error saving user bio to Firestore:', error);
      throw error;
    }
  };

  const value: OnboardingContextType = {
    state,
    setStep,
    setData,
    setLoading,
    setError,
    reset,
    goToNextStep,
    goToPreviousStep,
    saveUserBioToFirestore,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

// Hook to use the context
export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
